// Series primitive that draws the marks of an ACCEPTED model book annotation
// (docs/projects/MODEL_BOOK_EVOLUTION.md §7–8, Stage 2).
//
// Three mark types, all keyed on dates and a price so nothing here has to
// know which bar index a prompt happened to use:
//
//   callout — a small circle at (from_date, price) with the label beside it
//   level   — a horizontal line at `price` from from_date to to_date, label
//             at its right end
//   span    — two vertical ticks at from_date and to_date across the pane,
//             label centred above them
//
// PROVENANCE BY LINE STYLE (§6 item 14, TrendSpider's convention): every
// stroke here is DASHED, because every mark here is ASSERTED by a model
// rather than measured. Solid belongs to the detector's own geometry
// (vcp_markup_overlay.js); a third, distinct style stays reserved for
// human-drawn marks. Never make one of these solid — the line style is the
// only thing telling a reader which marks are measured.
//
// Coordinates go through timeScale().timeToCoordinate() and
// series.priceToCoordinate(); logicalToCoordinate() silently returns 0 for
// fractional logicals. A mark whose coordinates come back null is skipped
// rather than drawn at the left edge. Text has no built-in primitive, so
// labels are drawn with the same haloed fillText the markup overlay uses.

import { drawLabel, readTheme } from "./vcp_markup_overlay"

// One teal for every AI mark: the reader learns one colour plus one line
// style, not a palette per mark type.
export const MARK_COLORS = Object.freeze({ light: "#00897b", dark: "#4db6ac" })
export const MARK_DASH = Object.freeze([ 4, 3 ])
const MARK_FONT = "600 11px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
const CALLOUT_RADIUS = 5
const CALLOUT_LABEL_OFFSET = 10
const SPAN_ALPHA = 0.4

// The shapes worth drawing, normalised: a type we know, a label, the anchors
// each type needs. Pure, so the geometry is testable without a canvas.
export function markGeometry(shapes) {
  if (!Array.isArray(shapes)) return []

  return shapes.reduce((marks, shape) => {
    if (!shape || !shape.label || !shape.from_date) return marks
    const price = Number(shape.price)
    const label = String(shape.label)

    if (shape.type === "callout" && price > 0) {
      marks.push({ type: "callout", time: shape.from_date, price, label })
    } else if (shape.type === "level" && price > 0 && shape.to_date) {
      marks.push({ type: "level", from: shape.from_date, to: shape.to_date, price, label })
    } else if (shape.type === "span" && shape.to_date) {
      marks.push({ type: "span", from: shape.from_date, to: shape.to_date, label })
    }
    return marks
  }, [])
}

// The price extremes the marks need on screen, so a level drawn above the
// bars is not autoscaled off the top. Spans carry no price and contribute
// nothing. Returns null when nothing has a price.
export function markPriceRange(marks) {
  const prices = marks.filter(m => m.price > 0).map(m => m.price)
  if (prices.length === 0) return null
  return { minValue: Math.min(...prices), maxValue: Math.max(...prices) }
}

function inkFor(theme) {
  return MARK_COLORS[theme.dark ? "dark" : "light"]
}

// A label that would run off the pane's right edge is drawn ending at the
// anchor instead of starting there (marks cluster at the right edge, where
// the setup resolved), and a centred one is pulled back inside the pane.
// Exported for the test; it is pure.
export function fitLabel(ctx, text, x, width, align, pad = 4) {
  ctx.font = MARK_FONT
  const w = ctx.measureText(text).width
  if (align === "left" && x + w > width - pad) return { x: Math.max(pad + w, x - 2 * CALLOUT_LABEL_OFFSET), align: "right" }
  if (align === "center") {
    const half = w / 2
    return { x: Math.min(Math.max(x, pad + half), width - pad - half), align: "center" }
  }
  return { x, align }
}

class AnnotationMarksRenderer {
  constructor(data) { this._data = data }

  draw(target) {
    const { chart, series, marks, theme } = this._data
    if (!chart || !series || !marks || marks.length === 0) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const ink = inkFor(theme)
      const { height, width } = scope.mediaSize

      marks.forEach(mark => {
        if (mark.type === "callout") this._drawCallout(ctx, timeScale, series, mark, ink, theme, width)
        else if (mark.type === "level") this._drawLevel(ctx, timeScale, series, mark, ink, theme, width)
        else if (mark.type === "span") this._drawSpan(ctx, timeScale, mark, ink, theme, height, width)
      })

      ctx.setLineDash([])
    })
  }

  _drawCallout(ctx, timeScale, series, mark, ink, theme, width) {
    const x = timeScale.timeToCoordinate(mark.time)
    const y = series.priceToCoordinate(mark.price)
    if (x === null || y === null) return

    ctx.strokeStyle = ink
    ctx.lineWidth = 1.5
    ctx.setLineDash(MARK_DASH)
    ctx.beginPath()
    ctx.arc(x, y, CALLOUT_RADIUS, 0, Math.PI * 2)
    ctx.stroke()
    // Beside the circle, never on the candle it points at.
    const fit = fitLabel(ctx, mark.label, x + CALLOUT_RADIUS + CALLOUT_LABEL_OFFSET, width, "left")
    drawLabel(ctx, mark.label, fit.x, y, ink, theme.background, MARK_FONT, fit.align)
  }

  _drawLevel(ctx, timeScale, series, mark, ink, theme, width) {
    const x0 = timeScale.timeToCoordinate(mark.from)
    const x1 = timeScale.timeToCoordinate(mark.to)
    const y = series.priceToCoordinate(mark.price)
    if (x0 === null || x1 === null || y === null) return

    ctx.strokeStyle = ink
    ctx.lineWidth = 1.5
    ctx.setLineDash(MARK_DASH)
    ctx.beginPath()
    ctx.moveTo(x0, y)
    ctx.lineTo(x1, y)
    ctx.stroke()
    drawLabel(ctx, mark.label, Math.min(x1 + 6, width - 4), y - 8, ink, theme.background, MARK_FONT, "right")
  }

  _drawSpan(ctx, timeScale, mark, ink, theme, height, width) {
    const x0 = timeScale.timeToCoordinate(mark.from)
    const x1 = timeScale.timeToCoordinate(mark.to)
    if (x0 === null || x1 === null) return

    ctx.save()
    ctx.globalAlpha = SPAN_ALPHA
    ctx.strokeStyle = ink
    ctx.lineWidth = 1.5
    ctx.setLineDash(MARK_DASH)
    ctx.beginPath()
    ctx.moveTo(x0, 0)
    ctx.lineTo(x0, height)
    ctx.moveTo(x1, 0)
    ctx.lineTo(x1, height)
    ctx.stroke()
    ctx.restore()
    const fit = fitLabel(ctx, mark.label, (x0 + x1) / 2, width, "center")
    drawLabel(ctx, mark.label, fit.x, 24, ink, theme.background, MARK_FONT, fit.align)
  }
}

class PaneView {
  constructor(data) { this._data = data }

  update(data) { this._data = data }
  renderer() { return new AnnotationMarksRenderer(this._data) }
  zOrder() { return "top" }
}

export class VcpAnnotationMarksOverlay {
  // @param shapes the accepted annotation's mark list (overlay.annotation.shapes)
  constructor({ shapes }) {
    this._sharedData = {
      chart: null,
      series: null,
      marks: markGeometry(shapes),
      theme: readTheme()
    }
    this._paneViews = [ new PaneView(this._sharedData) ]
    this._requestUpdate = null
    this._onThemeChanged = () => {
      this._sharedData.theme = readTheme()
      this.updateAllViews()
      if (this._requestUpdate) this._requestUpdate()
    }
  }

  attached(param) {
    this._sharedData.chart = param.chart
    this._sharedData.series = param.series
    this._requestUpdate = param.requestUpdate
    this._sharedData.theme = readTheme()
    document.addEventListener("theme:changed", this._onThemeChanged)
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    document.removeEventListener("theme:changed", this._onThemeChanged)
    this._requestUpdate = null
    this._sharedData.chart = null
    this._sharedData.series = null
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }

  // Keep a marked price on screen. Returns null when no mark carries one, so
  // a chart of nothing but spans keeps the candles' own scale.
  autoscaleInfo() {
    const range = markPriceRange(this._sharedData.marks)
    return range ? { priceRange: range } : null
  }
}
